import { useState } from "react";
import { useTranslation } from "react-i18next";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";

import {
  ComponentCategory,
  PERFUME_BOTTLE_VOLUME,
  bottleVolumeLeft,
  defaultLanguage,
  isLanguage,
  type PerfumeIngredient,
} from "@/models";
import { useIngredientDiplayName } from "@/services";
import { addComponent, type PerfumeEditor } from "@/services/perfume-editor";
import {
  categoryBudget,
  getIngredientSuggestionIndex,
  normalizeIngredientName,
  styleAffinities,
  suggestIngredients,
  suggestedVolume,
  type CategoryName,
  type CategoryUsage,
  type IngredientSuggestion,
  type SuggestionReason,
  type SuggestionStyle,
} from "@/services/ingredient-suggestion";
import { LargeDialog } from "./LargeDialog";
import { IngredientPicture } from "./IngredientPicture";
import { PerfumeCategoryTitle } from "./PerfumeCategoryTitle";

export function presentIngredients(editor: PerfumeEditor): PerfumeIngredient[] {
  return editor.perfumeViewModel.components
    .map((c) => c.ingredient)
    .filter((i): i is PerfumeIngredient => i !== null);
}

// ingredients of `category` not in the perfume yet: what can be suggested
export function hasIngredientsToSuggest(editor: PerfumeEditor, category: ComponentCategory) {
  const present = new Set(presentIngredients(editor).map((i) => i.name));
  return editor.availableIngredients[category].some((i) => !present.has(i.name));
}

// live volume of the perfume being edited (the saved one lags behind, autosave)
export function perfumeVolumeOf(editor: PerfumeEditor): number {
  return editor.perfumeViewModel.components
    .filter((c) => c.ingredient !== null)
    .reduce((sum, c) => sum + c.volume, 0);
}

// the client fills a 100 ml bottle: nothing can be added to a full one
export function isBottleFull(editor: PerfumeEditor): boolean {
  return bottleVolumeLeft(perfumeVolumeOf(editor)) < 1;
}

function categoryUsageOf(editor: PerfumeEditor, category: ComponentCategory): CategoryUsage {
  const names = new Set<string>();
  let volume = 0;
  for (const component of editor.perfumeViewModel.components) {
    if (component.category !== category || component.ingredient === null) continue;
    volume += component.volume;
    names.add(component.ingredient.name);
  }
  return { volume, count: names.size };
}

// Mounted only while open. The suggestions are computed when it opens or when the client chooses
// another style, and stay in place when the client adds one of them; their volumes follow what is
// left of the category and of the 100 ml bottle.
export function IngredientSuggestionDialog({
  editor,
  category,
  onClose,
}: {
  editor: PerfumeEditor;
  category: ComponentCategory;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const index = getIngredientSuggestionIndex();
  const categoryName = ComponentCategory[category] as CategoryName;
  const usage = categoryUsageOf(editor, category);
  const perfumeVolume = perfumeVolumeOf(editor);
  const bottleLeft = bottleVolumeLeft(perfumeVolume);

  const suggest = (style: string | null) =>
    suggestIngredients({
      index,
      present: presentIngredients(editor),
      candidates: editor.availableIngredients[category],
      category: categoryName,
      style,
      usage: categoryUsageOf(editor, category),
      perfumeVolume: perfumeVolumeOf(editor),
    });
  const [style, setStyle] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<IngredientSuggestion[]>(() => suggest(null));
  // the styles closest to the perfume first, in the order of the opening
  const [styles] = useState(() =>
    styleAffinities(
      index,
      presentIngredients(editor).map((i) => i.name),
    ).map((affinity) => affinity.style),
  );
  // name → volume added
  const [added, setAdded] = useState<ReadonlyMap<string, number>>(() => new Map());

  const chooseStyle = (newStyle: string | null) => {
    if (newStyle === style) return;
    setStyle(newStyle);
    setSuggestions(suggest(newStyle));
  };

  const add = (ingredient: PerfumeIngredient, volume: number) => {
    addComponent(editor, { category, ingredient, volume, suggested: true });
    setAdded((volumes) => new Map(volumes).set(ingredient.name, volume));
  };

  const budget = categoryBudget(index, categoryName, usage);
  const availableIngredients = Object.values(editor.availableIngredients).flat();

  return (
    <LargeDialog
      open={true}
      onClose={onClose}
      title={
        <>
          {t("suggestionTitle")} · <PerfumeCategoryTitle category={category} quantity={2} noIcon />
        </>
      }
    >
      <Box sx={{ px: 3, pt: 2, pb: 1, borderBottom: 1, borderColor: "divider" }}>
        {budget && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <PerfumeCategoryTitle category={category} quantity={2} noIcon /> ·{" "}
            {budget.left > 0
              ? t("suggestionBudget", { used: Math.round(budget.used), usual: budget.usual })
              : t("suggestionBudgetFull", { usual: budget.usual })}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{ color: bottleLeft < 1 ? "error.main" : "text.secondary" }}
        >
          {bottleLeft < 1
            ? t("suggestionBottleFull", { bottle: PERFUME_BOTTLE_VOLUME })
            : t("suggestionBottleLeft", {
                volume: Math.round(bottleLeft),
                bottle: PERFUME_BOTTLE_VOLUME,
              })}
        </Typography>
        {styles.length > 0 && (
          <Stack
            direction="row"
            aria-label={t("suggestionStyles")}
            sx={{ gap: 1, py: 1, overflowX: "auto", flexWrap: "nowrap" }}
          >
            <Chip
              icon={<AutoAwesomeIcon />}
              label={t("suggestionForYou")}
              color="primary"
              variant={style === null ? "filled" : "outlined"}
              onClick={() => chooseStyle(null)}
            />
            {styles.map((s) => (
              <StyleChip
                key={s.id}
                style={s}
                availableIngredients={availableIngredients}
                selected={style === s.id}
                onClick={() => chooseStyle(s.id)}
              />
            ))}
          </Stack>
        )}
      </Box>
      <DialogContent>
        <Stack divider={<Divider />} sx={{ gap: 2 }}>
          {suggestions.map((suggestion) => {
            const name = suggestion.ingredient.name;
            const addedVolume = added.get(name);
            const volume =
              addedVolume ?? suggestedVolume(index, name, categoryName, usage, perfumeVolume);
            return (
              <SuggestionRow
                key={name}
                suggestion={suggestion}
                volume={volume}
                added={addedVolume !== undefined}
                onAdd={() => add(suggestion.ingredient, volume)}
              />
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("suggestionClose")}</Button>
      </DialogActions>
    </LargeDialog>
  );
}

function StyleChip({
  style,
  availableIngredients,
  selected,
  onClick,
}: {
  style: SuggestionStyle;
  availableIngredients: readonly PerfumeIngredient[];
  selected: boolean;
  onClick: () => void;
}) {
  const { i18n } = useTranslation();
  const ingredientDisplayName = useIngredientDiplayName();
  const language = isLanguage(i18n.resolvedLanguage) ? i18n.resolvedLanguage : defaultLanguage;
  const signature = style.signature
    .map((name) => availableIngredients.find((i) => normalizeIngredientName(i.name) === name))
    .filter((i): i is PerfumeIngredient => i !== undefined);
  const label =
    style.label?.[language] ||
    style.label?.[defaultLanguage] ||
    signature
      .slice(0, 2)
      .map((i) => ingredientDisplayName(i))
      .join(" · ");

  return (
    <Chip
      avatar={signature[0] && <Avatar alt="" src={`${signature[0].icon}-64.png`} />}
      label={label}
      color="primary"
      variant={selected ? "filled" : "outlined"}
      onClick={onClick}
    />
  );
}

function SuggestionRow({
  suggestion: { ingredient, reason },
  volume,
  added,
  onAdd,
}: {
  suggestion: IngredientSuggestion;
  volume: number;
  added: boolean;
  onAdd: () => void;
}) {
  // the bottle is full: nothing can be added
  const full = !added && volume < 1;
  const { t } = useTranslation();
  const ingredientDisplayName = useIngredientDiplayName();
  const description = useIngredientDescription(ingredient);

  return (
    <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
      <IngredientPicture ingredient={ingredient} size="icon" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6">{ingredientDisplayName(ingredient)}</Typography>
        {description && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </Typography>
        )}
        {reason && <SuggestionReasonText reason={reason} />}
        {!full && <Typography variant="body2">{t("suggestionVolume", { volume })}</Typography>}
      </Box>
      <Button
        variant={added ? "text" : "contained"}
        disabled={added || full}
        startIcon={added ? <CheckIcon /> : <AddIcon />}
        onClick={onAdd}
        sx={{ flexShrink: 0 }}
      >
        {added ? t("suggestionAdded") : t("suggestionAdd")}
      </Button>
    </Stack>
  );
}

function SuggestionReasonText({ reason }: { reason: SuggestionReason }) {
  const { t, i18n } = useTranslation();
  const ingredientDisplayName = useIngredientDiplayName();

  const text =
    reason.kind === "pairedWith"
      ? t("suggestionPairedWith", {
          ingredients: new Intl.ListFormat(i18n.resolvedLanguage, { type: "conjunction" }).format(
            reason.ingredients.map((i) => ingredientDisplayName(i)),
          ),
        })
      : reason.kind === "style"
        ? t("suggestionStyleReason", { percent: reason.percent })
        : t("suggestionPopular", { percent: reason.percent });

  return (
    <Typography variant="body2" sx={{ fontStyle: "italic" }}>
      {text}
    </Typography>
  );
}

function useIngredientDescription(ingredient: PerfumeIngredient): string {
  const { i18n } = useTranslation();
  const language = isLanguage(i18n.resolvedLanguage) ? i18n.resolvedLanguage : defaultLanguage;
  return ingredient.description?.[language] || ingredient.description?.[defaultLanguage] || "";
}
